import { useEffect, useMemo, useRef, useState } from "react";
import constants from "../../../../constants";
import { getStorage, setStorage } from "utils";
import {
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
  OFFICE_NAV_CONTEXT_CHANGE_EVENT,
} from "utils/officeNavigationContext";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

const useDashBoardModule = () => {
  const cacheDashBoardFilter = constants.notes.cache.dashboardFilter;
  const cacheFilters = getStorage(cacheDashBoardFilter);
  const isFromOwnerOfficeModule = getStorage("isFromOwnerOfficeModule");
  const enterpriseCache = getStorage(
    constants.localStorageKeys.agenda.staffSchedulerEnterprise,
  );
  const {
    ownerList,
    commonOwnerData: selectedOwner,
    isOwner: isAccountOwner,
    effectiveOwnerId: ownerId,
    subscriptionValues,
    isStaff,
    profile,
  } = useAccountOwnerCommon();
  const isFilterApply =
    cacheFilters?.offices?.length > 0 ||
    cacheFilters?.staff?.length > 0 ||
    cacheFilters?.agenda?.length > 0 ||
    cacheFilters?.viewSettig?.length > 0 ||
    isFromOwnerOfficeModule?.officeId;
  const [officeFilter, setOfficeFilter] = useState(cacheFilters?.offices || []);
  const [staffFilter, setStaffFilter] = useState(cacheFilters?.staff || []);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [appliedOfficeFilters, setAppliedOfficeFilters] = useState(
    cacheFilters?.offices || [],
  );
  const [appliedStaffFilters, setAppliedStaffFilters] = useState(
    cacheFilters?.staff || [],
  );
  const [currentDate, onDateChange] = useState(new Date());
  const [agendaTypes, setAgendaTypes] = useState(cacheFilters?.agenda || []);
  const [appliedAgendaTypes, setappliedAgendaTypes] = useState(
    cacheFilters?.agenda || [],
  );
  const [viewSettig, setViewSetting] = useState(cacheFilters?.viewSettig || []);
  const [appliedViewSettig, setappliedViewSettig] = useState(
    cacheFilters?.viewSettig || [],
  );
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isSchedulerOnly, setIsSchedulerOnly] = useState(
    cacheFilters?.isScheduler || false,
  );
  // Add state for showEnterpriseEvents
  const [showEnterpriseEvents, setShowEnterpriseEvents] = useState(
    enterpriseCache || false,
  );
  // Add state for when the filter is actually applied
  const [isEnterpriseFilterActive, setIsEnterpriseFilterActive] = useState(
    enterpriseCache || false,
  );
  const [userList, setUserList] = useState([]);
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);
  const hasUserAppliedFilterThisSession = useRef(false);

  const scrollToElement = () => {
    const element = document.getElementById("managerDashboardTargetElement");
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "start",
      });
    }
  };

  useEffect(() => {
    setStorage(constants.notes.cache.isChatFromDashBoard, true);
  }, []);

  useEffect(() => {
    const officeContext = getOfficeNavigationContext();
    if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
      setOfficeFilter([officeContext.officeId]);
      setAppliedOfficeFilters([officeContext.officeId]);
    }
  }, []);

  useEffect(() => {
    const syncOfficeFiltersFromContext = () => {
      const officeContext = getOfficeNavigationContext();
      if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
        setOfficeFilter([officeContext.officeId]);
        setAppliedOfficeFilters([officeContext.officeId]);
        return;
      }
      const cached = getStorage(cacheDashBoardFilter) || {};
      setOfficeFilter(cached?.offices || []);
      setAppliedOfficeFilters(cached?.offices || []);
    };
    window.addEventListener(
      OFFICE_NAV_CONTEXT_CHANGE_EVENT,
      syncOfficeFiltersFromContext,
    );
    return () =>
      window.removeEventListener(
        OFFICE_NAV_CONTEXT_CHANGE_EVENT,
        syncOfficeFiltersFromContext,
      );
  }, []);

  const handleApplyFilter = () => {
    const hasFilters =
      officeFilter?.length > 0 ||
      staffFilter?.length > 0 ||
      agendaTypes?.length > 0 ||
      viewSettig?.length > 0;

    if (hasFilters) {
      hasUserAppliedFilterThisSession.current = true;
      scrollToElement();
    }

    setAppliedOfficeFilters(officeFilter);
    setAppliedStaffFilters(staffFilter);
    setappliedAgendaTypes(agendaTypes);
    setappliedViewSettig(viewSettig);
    setIsEnterpriseFilterActive(showEnterpriseEvents);
    setStorage(
      constants.localStorageKeys.agenda.staffSchedulerEnterprise,
      showEnterpriseEvents,
    );
    setStorage(cacheDashBoardFilter, {
      offices: officeFilter,
      staff: staffFilter,
      agenda: agendaTypes,
      viewSettig: viewSettig,
      isScheduler: isSchedulerOnly,
    });
  };

  const onActiveStartDateChange = (value) => {
    if (
      constants.calanderActions.includes(value?.action) ||
      (value?.action === constants.calanderActionKey.drillDown &&
        (value?.view === "month" || value?.view === "year"))
    ) {
      value?.activeStartDate && onDateChange(value.activeStartDate);
    }
  };

  const resetFilter = () => {
    if (isFilterApply && hasUserAppliedFilterThisSession.current)
      scrollToElement();
    hasUserAppliedFilterThisSession.current = false;
    setOfficeFilter([]);
    setStaffFilter([]);
    setSelectedRoles([]);
    setResetSearchTrigger((prev) => prev + 1);
    setAgendaTypes([]);
    setViewSetting([]);
    setAppliedOfficeFilters([]);
    setAppliedStaffFilters([]);
    setappliedAgendaTypes([]);
    setappliedViewSettig([]);
    setIsSchedulerOnly(false);
    setIsEnterpriseFilterActive(false);
    setShowEnterpriseEvents(false);
    setStorage(
      constants.localStorageKeys.agenda.staffSchedulerEnterprise,
      false,
    );
    setStorage(cacheDashBoardFilter, {
      offices: [],
      staff: [],
      agenda: [],
      viewSettig: [],
      isScheduler: false,
    });
  };

  const preservedRef = useRef(null);
  useEffect(() => {
    if (preservedRef.current === null) {
      preservedRef.current = ownerId;
    } else if (preservedRef.current !== ownerId) {
      resetFilter();
    }
  }, [ownerId]);
  /** Toggle All Modules vs Scheduler Only without clearing filter selections or applied filters. */
  const handleToggleScheduler = (e) => {
    const nextSchedulerOnly = e.target.checked;
    setIsSchedulerOnly(nextSchedulerOnly);
    setStorage(cacheDashBoardFilter, {
      offices: officeFilter,
      staff: staffFilter,
      agenda: agendaTypes,
      viewSettig,
      isScheduler: nextSchedulerOnly,
    });
  };

  const handleToggleAccountOwner = () => {
    setIsAdminModalOpen(!isAdminModalOpen);
  };

  const StaffDataForApi = useMemo(() => {
    return userList.reduce(
      (prev, curr) => {
        const value = appliedStaffFilters.some((res) => res.id === curr.id);
        const id = curr.userId ?? curr.id;
        if (value && !prev.keyPair.has(id)) {
          prev.returnedArray.push(id);
          prev.keyPair.set(id, id);
        }
        return prev;
      },
      {
        returnedArray: [],
        keyPair: new Map(),
      },
    ).returnedArray;
  }, [appliedStaffFilters, userList]);
  return {
    data: {
      officeFilter,
      staffFilter,
      agendaTypes,
      viewSettig,
      selectedRoles,
      appliedOfficeFilters,
      appliedStaffFilters: StaffDataForApi,
      otherStaffId: appliedStaffFilters,
      appliedAgendaTypes,
      appliedViewSettig,
      ownerId,
      ownerData: selectedOwner,
      isFilterApply,
      currentDate,
      ownerList,
      selectedOwner,
      isAdminModalOpen,
      isSchedulerOnly,
      userList,
      isEnterpriseFilterActive,
      showEnterpriseEvents,
      subscriptionValues,
      isAccountOwner,
      isStaff,
      profile,
    },
    methods: {
      setOfficeFilter,
      setStaffFilter,
      setSelectedRoles,
      setAgendaTypes,
      setViewSetting,
      setappliedViewSettig,
      handleApplyFilter,
      resetFilter,
      resetSearchTrigger,
      setUserList,
      onDateChange,
      onActiveStartDateChange,
      handleToggleAccountOwner,
      setIsSchedulerOnly,
      handleToggleScheduler,
      setIsEnterpriseFilterActive,
      setShowEnterpriseEvents,
    },
  };
};

export default useDashBoardModule;
